import { memo, useCallback, useEffect, useMemo, useRef } from "react";
import { useSearchParams } from "react-router-dom";

import usePolledLazyQuery from "apollo/usePolledQuery/usePolledLazyQuery";
import Filters from "components/Filters";
import FiltersContentWrapper from "components/Filters/ContentWrapper";
import {
  makeFilterItemOptionsFromSuggestionField,
  setUpdatedFiltersUrlParams,
} from "components/Filters/helpers";
import { useCachedFilterFields } from "components/Filters/hooks";
import FiltersSidebar from "components/Filters/Sidebar";
import FiltersSplit from "components/Filters/Split";
import {
  FilterItem,
  FiltersItemsOptionsMap,
  SavedFilterView,
  SortOption,
} from "components/Filters/types";
import FlashContext from "components/FlashMessages/FlashContext";
import { getSearchQuery } from "components/SearchInput/helpers";
import { SEARCH_STACK_SUGGESTIONS_DICTIONARY } from "constants/stack";
import { URL_FILTER_TAB_KEY } from "constants/url_query_keys";
import { AnalyticsPageStack } from "hooks/useAnalytics/pages/stack";
import useTypedContext from "hooks/useTypedContext";
import useTypedFlags from "hooks/useTypedFlags";
import useURLParams from "hooks/useURLParams";
import {
  SearchQueryPredicate,
  SearchSuggestionsFieldType,
  SearchSuggestionsOutput,
} from "types/generated";

import { PollingIntervalGroups } from "../../../../apollo/constants";
import {
  FILTER_TYPE,
  FILTERS_ORDER_SETTINGS_KEY,
  initialSortDirection,
  initialSortOption,
} from "../constants";
import { SEARCH_STACKS_SUGGESTIONS } from "../gql";
import { getFilterKey } from "../helpers";
import { PredefinedFilterType } from "../usePredefinedFilters";
import { PROVIDER_FILTER_NAME } from "./constants";
import StackFiltersLayoutSyncWithPredefinedTabs from "./SyncWithPredefinedTabs";
import StackFiltersLayoutTabNavigation from "./TabNavigation";

type StackFiltersLayoutProps = {
  predicates: SearchQueryPredicate[];
  children: React.ReactNode;
  currentSavedView?: SavedFilterView;
  setCurrentSavedView: (view?: SavedFilterView) => unknown;
  setItemsCount: (count: number) => void;
  itemsCount: number;
  applyFilterCallback?: () => void;
};

const DEPRECATED_FILTER_NAMES = [
  PROVIDER_FILTER_NAME, // can be removed once the filter is removed from backend
];

const StackFiltersLayout = ({
  predicates,
  children,
  currentSavedView,
  setCurrentSavedView,
  setItemsCount,
  itemsCount,
  applyFilterCallback,
}: StackFiltersLayoutProps) => {
  const { stackListTagColumn } = useTypedFlags();
  const fieldsRef = useRef<string[] | null>(null);

  const urlParams = useURLParams();
  const searchInput = getSearchQuery(urlParams);

  const { onError } = useTypedContext(FlashContext);

  const [loadSearchStacksSuggestions, { data: filteringData, loading, refetch }] =
    usePolledLazyQuery<{
      searchStacksSuggestions: SearchSuggestionsOutput;
    }>(SEARCH_STACKS_SUGGESTIONS, { pollingGroup: PollingIntervalGroups.Lists });

  const cachedFilters = useCachedFilterFields(filteringData?.searchStacksSuggestions?.fields);

  const cachedFiltersData = useMemo(() => {
    return cachedFilters.filter((field) => stackListTagColumn || field.name !== "tag");
  }, [cachedFilters, stackListTagColumn]);

  const sortOptions = useMemo((): SortOption[] => {
    return (
      cachedFiltersData
        .filter((field) => field.orderable)
        .map((field) => {
          return {
            value: field.name,
            // TODO: distinguish labels from keys
            label: getFilterKey(field.name),
          };
        }) || []
    );
  }, [cachedFiltersData]);

  const [filters, stacksFiltersItemsOptionsMap] = useMemo<
    [FilterItem[], FiltersItemsOptionsMap]
  >(() => {
    let labelsCounter = 0;
    const filtersItemsOptionsMap: FiltersItemsOptionsMap = new Map([]);

    return [
      cachedFiltersData
        .filter((field) => field.filterable)
        .map((field) => {
          let key = field.name;

          if (key === "label") {
            labelsCounter += 1;
            key = `label${labelsCounter}`;
          } else {
            key = getFilterKey(key);
          }

          const options = makeFilterItemOptionsFromSuggestionField(field);

          if (options) {
            filtersItemsOptionsMap.set(field.name, options);
          }

          return {
            key,
            filterName: field.name,
            //SearchSuggestionsFieldType is only available if the field is filterable
            type: field.type as SearchSuggestionsFieldType,
          };
        }) || [],
      filtersItemsOptionsMap,
    ];
  }, [cachedFiltersData]);

  const loadSearchSuggestions = (initial = false) => {
    try {
      loadSearchStacksSuggestions({
        variables: {
          input: {
            fullTextSearch: searchInput,
            predicates,
            fields: initial ? null : fieldsRef.current,
          },
        },
        onCompleted: (data) => {
          if (
            data?.searchStacksSuggestions.filteredCount &&
            data?.searchStacksSuggestions.filteredCount !== itemsCount
          ) {
            setItemsCount(data?.searchStacksSuggestions.filteredCount);
          }
        },
      });
    } catch (error) {
      onError(error);
    }
  };

  const handlePollingActiveSections = (fields: string[]) => {
    if (fields.length) {
      fieldsRef.current = fields;

      refetch?.({
        input: {
          fullTextSearch: searchInput,
          predicates,
          fields,
        },
      }).then(({ data }) => {
        if (
          data?.searchStacksSuggestions.filteredCount &&
          data?.searchStacksSuggestions.filteredCount !== itemsCount
        ) {
          setItemsCount(data?.searchStacksSuggestions.filteredCount);
        }
      });
    }
  };

  const tab = urlParams.get(URL_FILTER_TAB_KEY);

  const disabledFilters = useMemo(() => {
    if (tab && tab !== PredefinedFilterType.AllStacks) {
      return ["state"];
    }

    return undefined;
  }, [tab]);

  const [searchParams, setSearchParams] = useSearchParams();

  const resetPredefinedTab = useCallback(() => {
    if (searchParams.has(URL_FILTER_TAB_KEY)) {
      searchParams.delete(URL_FILTER_TAB_KEY);

      const updatedParams = setUpdatedFiltersUrlParams(
        new Map(),
        searchParams,
        sessionStorage,
        FILTERS_ORDER_SETTINGS_KEY
      );

      setSearchParams(updatedParams);
    }

    applyFilterCallback?.();
  }, [searchParams, setSearchParams, applyFilterCallback]);

  // initial request with nullish fields
  useEffect(() => {
    loadSearchSuggestions(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    loadSearchSuggestions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [predicates, searchInput]);

  return (
    <Filters
      filters={filters}
      filtersItemsOptionsMap={stacksFiltersItemsOptionsMap}
      filtersLoading={loading}
      sortOptions={sortOptions}
      initialSortOption={initialSortOption}
      initialSortDirection={initialSortDirection}
      filtersDictionary={SEARCH_STACK_SUGGESTIONS_DICTIONARY}
      pollActiveSections={handlePollingActiveSections}
      filtersOrderSettingsKey={FILTERS_ORDER_SETTINGS_KEY}
      filtersType={FILTER_TYPE}
      currentSavedView={currentSavedView}
      deprecatedFilterNames={DEPRECATED_FILTER_NAMES}
      setCurrentSavedView={setCurrentSavedView}
      applyFilterCallback={applyFilterCallback}
      disabledFilters={disabledFilters}
      resetFiltersCallback={resetPredefinedTab}
    >
      <StackFiltersLayoutTabNavigation />
      <FiltersSplit role="tabpanel" id={`tabpanel-${tab || PredefinedFilterType.AllStacks}`}>
        <StackFiltersLayoutSyncWithPredefinedTabs />
        <FiltersSidebar analyticsPage={AnalyticsPageStack.StacksList} />

        <FiltersContentWrapper>{children}</FiltersContentWrapper>
      </FiltersSplit>
    </Filters>
  );
};

export default memo(StackFiltersLayout);
